import React from 'react';
import PropTypes from 'prop-types';
import { Container, Stack, Divider } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Rating } from '@fiverr-private/orca';
import { I18n } from '@fiverr-private/i18n-react';
import Description from '../../../../shared/Description';
import SellerResponse from '../../SellerResponse';
import TranslationButton from '../../../../shared/TranslationButton';
import { GigInfo, GigInfoPropType } from '../../../../FreelancerReviews/FreelancerReviewItem/GigInfo';
import { WorkSample } from '../../../../shared/WorkSample';
import { ReliableReviewHeader } from './ReliableReviewHeader';
import { scoreClass, wrapperClass } from './ReliableReviewItem.css';

const MobileView = ({
    user,
    createdAt,
    showBusinessBadge,
    isRepeatBuyer,
    isCancelledOrder,
    countryCode,
    ratingScore,
    descriptionText,
    shouldHighlight,
    priceRange,
    orderDuration,
    orderTypeHourly,
    displayTranslationButton,
    translationConfig,
    componentName,
    workSampleData,
    gigData,
    sellerResponse,
}) => (
    <Container marginTop="8" borderWidth="sm" borderRadius="2xl" className={wrapperClass}>
        <Stack withDivider gap="4" paddingX="4" paddingTop="4" paddingBottom="6" direction="column">
            <ReliableReviewHeader
                user={user}
                showBusinessBadge={showBusinessBadge}
                isRepeatBuyer={isRepeatBuyer}
                countryCode={countryCode}
            />
            <Stack gap="0" direction="column" justifyContent="spaceBetween">
                <Stack gap="2" direction="column">
                    {isCancelledOrder && (
                        <Text color="bodySecondary" size="b_xs">
                            <I18n k="reviews.general.order_canceled" />
                        </Text>
                    )}
                    <Stack withDivider direction="row" alignItems="center" dividerProps={{ variant: 'dot' }}>
                        <Rating score={ratingScore} color={Rating.COLORS.BLACK} scoreClassName={scoreClass} />
                        <Text size="b_xs" color="bodySecondary">
                            <time>{createdAt}</time>
                        </Text>
                    </Stack>
                    <Description text={descriptionText} shouldHighlight={shouldHighlight} maxLength={180} displayLess />
                    {(priceRange || orderDuration || orderTypeHourly || gigData || workSampleData) && (
                        <Stack marginTop="2" gap="1" direction="column">
                            {priceRange ? <Stat value={priceRange} title={<I18n k="reviews.gigs.price" />} /> : null}
                            {orderDuration ? (
                                <Stat value={orderDuration} title={<I18n k="reviews.gigs.duration" />} />
                            ) : null}
                            {orderTypeHourly ? (
                                <Stat
                                    value={<I18n k="reviews.gigs.order_type_hourly" />}
                                    title={<I18n k="reviews.gigs.order_type" />}
                                />
                            ) : null}
                            {workSampleData?.previewUrl ? (
                                <Stack direction="column" marginTop="4">
                                    <WorkSample {...workSampleData} inlineVideoPlayer />
                                </Stack>
                            ) : null}
                            {gigData ? <GigInfo gig={gigData} /> : null}
                        </Stack>
                    )}
                    {displayTranslationButton ? (
                        <Container marginTop="4">
                            <TranslationButton translationConfig={translationConfig} componentName={componentName} />
                        </Container>
                    ) : null}
                </Stack>
            </Stack>
        </Stack>
        {sellerResponse ? (
            <Stack direction="column" paddingX="6" gap="0">
                <Divider />
                <SellerResponse response={sellerResponse} ratingScore={ratingScore} />
            </Stack>
        ) : null}
    </Container>
);

MobileView.propTypes = {
    user: PropTypes.object.isRequired,
    createdAt: PropTypes.string.isRequired,
    showBusinessBadge: PropTypes.bool.isRequired,
    countryCode: PropTypes.string.isRequired,
    ratingScore: PropTypes.number.isRequired,
    descriptionText: PropTypes.string.isRequired,
    shouldHighlight: PropTypes.bool.isRequired,
    priceRange: PropTypes.string,
    orderDuration: PropTypes.string,
    orderTypeHourly: PropTypes.bool,
    displayTranslationButton: PropTypes.bool.isRequired,
    isRepeatBuyer: PropTypes.bool.isRequired,
    isCancelledOrder: PropTypes.bool.isRequired,
    translationConfig: PropTypes.object.isRequired,
    componentName: PropTypes.string.isRequired,
    workSampleData: PropTypes.shape({
        workSample: PropTypes.string,
        smallWorkSample: PropTypes.string,
        position: PropTypes.number,
    }),
    gigData: GigInfoPropType,
    sellerResponse: PropTypes.object,
};

const Stat = ({ value, title }) => (
    <Stack gap="0" direction="rowReverse" justifyContent="flexEnd">
        <Text fontWeight="semibold" size="b_sm">
            {value}
        </Text>
        <Text color="bodySecondary" size="b_sm" minWidth="90px">
            {title}
        </Text>
    </Stack>
);

export default MobileView;
